import { useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  TransformWrapper,
  TransformComponent,
  useControls,
} from "react-zoom-pan-pinch";
import { attachmentContentUrl, attachmentPreviewSrc } from "../api/attachments";
import { AttachmentPreviewEditor } from "./AttachmentPreviewEditor";
import type { AttachmentDto } from "../api/types";

export type AttachmentPreviewInlineProps = {
  attachment: AttachmentDto;
};

const overlayButtonSx = {
  color: "#fff",
  bgcolor: "rgba(0, 0, 0, 0.5)",
  "&:hover": { bgcolor: "rgba(0, 0, 0, 0.75)" },
} as const;

const ZoomControls = () => {
  const { zoomIn, zoomOut, resetTransform } = useControls();

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ position: "absolute", bottom: 8, right: 8, zIndex: 1 }}
    >
      <Tooltip title="Zoom out">
        <IconButton
          size="small"
          aria-label="Zoom out"
          onClick={() => zoomOut()}
          sx={overlayButtonSx}
        >
          <ZoomOutIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Zoom in">
        <IconButton
          size="small"
          aria-label="Zoom in"
          onClick={() => zoomIn()}
          sx={overlayButtonSx}
        >
          <ZoomInIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Fit to view">
        <IconButton
          size="small"
          aria-label="Fit to view"
          onClick={() => resetTransform()}
          sx={overlayButtonSx}
        >
          <FitScreenIcon />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

/**
 * Stand-in for attachments with no preview to show. A bare `<img src=…>` would
 * show a broken-image icon and log a 404, so the viewer shows the filename, a
 * download link, and the way out: making a preview here and now.
 */
const NoPreviewPlaceholder = ({ attachment }: AttachmentPreviewInlineProps) => (
  <Stack
    spacing={1.5}
    sx={{
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      color: "grey.400",
      p: 3,
      textAlign: "center",
    }}
  >
    <InsertDriveFileIcon sx={{ fontSize: 64 }} />
    <Typography variant="body1" sx={{ color: "grey.300" }}>
      No preview available
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
      {attachment.filename}
    </Typography>
    <Stack direction="row" spacing={1}>
      <Button
        component="a"
        href={attachmentContentUrl(attachment)}
        download={attachment.filename}
        variant="outlined"
        size="small"
        startIcon={<DownloadIcon />}
        sx={{ color: "grey.300", borderColor: "grey.600" }}
      >
        Download original
      </Button>
      <AttachmentPreviewEditor attachment={attachment} onDark />
    </Stack>
  </Stack>
);

export const AttachmentPreviewInline = ({
  attachment,
}: AttachmentPreviewInlineProps) => {
  // A row can claim a preview the bucket doesn't have — content replaced
  // without a new one, or a write that never landed. Remembering *which* src
  // failed rather than a bare boolean means a regenerated preview (new version,
  // new URL) is tried again without any reset.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const src = attachmentPreviewSrc(attachment);
  const previewUrl = src === failedSrc ? undefined : src;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 400,
        bgcolor: "#000",
        display: "flex",
        overflow: "hidden",
        borderRadius: 1,
      }}
    >
      {previewUrl && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
        >
          <AttachmentPreviewEditor attachment={attachment} variant="icon" />
          <Tooltip title="Download original">
            <IconButton
              component="a"
              href={attachmentContentUrl(attachment)}
              download={attachment.filename}
              aria-label={`Download ${attachment.filename}`}
              sx={overlayButtonSx}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      {previewUrl ? (
        <TransformWrapper centerOnInit minScale={0.1} key={attachment.id}>
          <ZoomControls />
          <TransformComponent
            wrapperStyle={{ width: "100%", height: "100%" }}
            contentStyle={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              src={previewUrl}
              alt={attachment.filename}
              onError={() => setFailedSrc(previewUrl)}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </TransformComponent>
        </TransformWrapper>
      ) : (
        <NoPreviewPlaceholder attachment={attachment} />
      )}
    </Box>
  );
};
